import { Link } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';

const productLinks = [
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Sign In', href: '/login', isRoute: true },
];

const legalLinks = [
  { label: 'Privacy Policy', href: '/privacy', isRoute: true },
  { label: 'Terms of Service', href: '/terms', isRoute: true },
];

const socialLinks = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/zupapost',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: 'X (Twitter)',
    href: 'https://x.com/zupa938240',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/zupa_ai/',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
      </svg>
    ),
  },
];

export function FooterSection() {
  const linkStyle = {
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    fontSize: '1rem',
  };

  return (
    <footer
      id="contact"
      className="relative bg-landing-bg-alt"
      style={{
        borderTop: '1px solid rgba(232, 230, 240, 0.06)',
        paddingTop: 'clamp(60px, 8vh, 100px)',
        paddingBottom: 'clamp(30px, 4vh, 60px)',
      }}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* 4-column grid */}
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Column 1: Logo + tagline */}
          <div>
            <Link to="/" className="inline-block">
              <Logo variant="icon-wordmark" className="h-10" inverted />
            </Link>
            <p
              className="mt-4 text-[#C8C6D2] max-w-[30ch]"
              style={{
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                fontSize: '1rem',
                lineHeight: 1.6,
              }}
            >
              Psychology-powered content generation.
            </p>
          </div>

          {/* Column 2: Product */}
          <div>
            <h4
              className="mb-4 text-[#E8E6F0]"
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Product
            </h4>
            <ul className="space-y-3">
              {productLinks.map((link) =>
                link.isRoute ? (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-[#C8C6D2] transition-colors duration-200 hover:text-zupa-purple-light"
                      style={linkStyle}
                    >
                      {link.label}
                    </Link>
                  </li>
                ) : (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-[#C8C6D2] transition-colors duration-200 hover:text-zupa-purple-light"
                      style={linkStyle}
                    >
                      {link.label}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Column 3: Legal */}
          <div>
            <h4
              className="mb-4 text-[#E8E6F0]"
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Legal
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-[#C8C6D2] transition-colors duration-200 hover:text-zupa-purple-light"
                    style={linkStyle}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Social */}
          <div>
            <h4
              className="mb-4 text-[#E8E6F0]"
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Social
            </h4>
            <ul className="space-y-3">
              {socialLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#C8C6D2] transition-colors duration-200 hover:text-zupa-purple-light"
                    style={linkStyle}
                  >
                    {link.icon}
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="mt-12 flex flex-col items-center justify-between gap-4 pt-6 sm:flex-row"
          style={{ borderTop: '1px solid rgba(232, 230, 240, 0.06)' }}
        >
          <p
            className="text-[#C8C6D2]/60"
            style={{
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              fontSize: '1rem',
            }}
          >
            &copy; {new Date().getFullYear()} Summerhill Technologies. All rights reserved.
          </p>
          <p
            className="text-[#C8C6D2]/40"
            style={{
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              fontSize: '0.875rem',
            }}
          >
            Built by Summerhill Technologies
          </p>
        </div>
      </div>
    </footer>
  );
}
